import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  apiMessage,
  ALL_CREATED_RANGE,
  CREATED_RANGE_OPTIONS,
  createProgressPreview,
  createdRangeQuery,
  createdRangeValue,
  createdWindow,
  formatLocalDate,
  listTechnicians,
  parseLocalDateParam,
  sendProgressPreview,
  tenantOptionLabel,
} from '../services/jobAuditApi';
import TechnicianScoreGauge from './TechnicianScoreGauge';
import { ProgressEmailModal } from './JobAuditProgressEmail';

const FILTER_CLASS =
  'mt-1 block w-40 rounded-md border border-gray-300 bg-white px-2 py-1.5 shadow-sm';

const PAGE_SIZE = 50;

const JobAuditTechnicians = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tenant = searchParams.get('tenant') || '';
  const confidence = searchParams.get('confidence') || '';
  const maxScore = searchParams.get('max_score') || '';
  const rangeParam = searchParams.get('range') || '';
  const on = searchParams.get('on') || '';
  const rangeSelect = createdRangeValue(rangeParam, on);
  const range = createdRangeQuery(rangeParam, on);
  const offset = Math.max(0, parseInt(searchParams.get('offset') || '0', 10) || 0);
  const onDate = parseLocalDateParam(on);

  const [loading, setLoading] = useState(true);
  const [nameQuery, setNameQuery] = useState('');
  const [data, setData] = useState({ total: 0, items: [], tenants: [] });
  const [emailTarget, setEmailTarget] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewError, setPreviewError] = useState('');

  const loadPreview = useCallback((row) => {
    setEmailTarget(row);
    setPreview(null);
    setPreviewError('');
    setPreviewLoading(true);
    createProgressPreview({ tenantId: row.tenant_code, technicianId: row.technician_id })
      .then((payload) => setPreview(payload))
      .catch((err) => setPreviewError(apiMessage(err, 'Could not build the progress email')))
      .finally(() => setPreviewLoading(false));
  }, []);

  const setFilter = useCallback(
    (patch) => {
      const next = new URLSearchParams(searchParams);
      Object.entries(patch).forEach(([key, value]) => {
        if (value) next.set(key, value);
        else next.delete(key);
      });
      if (!('offset' in patch)) next.delete('offset');
      setSearchParams(next);
    },
    [searchParams, setSearchParams]
  );

  useEffect(() => {
    let cancelled = false;
    const bounds = createdWindow({ range, on });
    setLoading(true);
    listTechnicians({
      tenant,
      created_after: bounds.created_after || '',
      created_before: bounds.created_before || '',
      confidence,
      max_score: maxScore,
      limit: PAGE_SIZE,
      offset,
    })
      .then((payload) => {
        if (!cancelled) setData(payload);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(apiMessage(err, 'Failed to load technician queue'));
          setData({ total: 0, items: [], tenants: [] });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenant, confidence, maxScore, range, on, offset]);

  const nameNeedle = nameQuery.trim().toLowerCase();
  const visibleItems = (data.items || []).filter((row) =>
    !nameNeedle || (row.technician || '').toLowerCase().includes(nameNeedle)
  );
  const showingFrom = data.total === 0 ? 0 : offset + 1;
  const showingTo = Math.min(offset + (data.items?.length || 0), data.total);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Technicians</h1>
        <p className="mt-1 text-sm text-gray-600">
          Supervisor queue of recent HHRC audits by score, tenant, and technician rollups.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-lg bg-white p-4 shadow">
        <label className="text-sm text-gray-700">
          Technician
          <input
            type="search"
            placeholder="name on this page"
            className="mt-1 block w-56 rounded-md border border-gray-300 bg-white px-2 py-1.5 shadow-sm"
            value={nameQuery}
            onChange={(e) => setNameQuery(e.target.value)}
          />
        </label>
        <label className="text-sm text-gray-700">
          Tenant
          <select
            className="mt-1 block w-56 rounded-md border border-gray-300 bg-white px-2 py-1.5 shadow-sm"
            value={tenant}
            onChange={(e) => setFilter({ tenant: e.target.value })}
          >
            <option value="">all</option>
            {(data.tenants || []).map((option) => (
              <option key={option.code || option} value={option.code || option}>
                {tenantOptionLabel(option)}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm text-gray-700">
          Confidence
          <select
            className={FILTER_CLASS}
            value={confidence}
            onChange={(e) => setFilter({ confidence: e.target.value })}
          >
            <option value="">all bands</option>
            <option value="low">Low (&lt;50)</option>
            <option value="medium">Medium (50–74)</option>
            <option value="high">High (≥75)</option>
          </select>
        </label>
        <label className="text-sm text-gray-700">
          Maximum score
          <input
            type="number"
            min="0"
            max="100"
            step="1"
            placeholder="0–100"
            className={FILTER_CLASS}
            value={maxScore}
            onChange={(e) => setFilter({ max_score: e.target.value })}
          />
        </label>
        <label className="text-sm text-gray-700">
          Created
          <select
            className={FILTER_CLASS}
            value={rangeSelect}
            onChange={(e) => setFilter({ range: e.target.value, on: '' })}
          >
            <option value={ALL_CREATED_RANGE}>all</option>
            {CREATED_RANGE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm text-gray-700">
          Date
          <DatePicker
            selected={onDate}
            onChange={(date) => setFilter({ on: date ? formatLocalDate(date) : '', range: '' })}
            dateFormat="yyyy-MM-dd"
            isClearable
            placeholderText="pick a day"
            wrapperClassName="mt-1 block"
            className="block w-40 rounded-md border border-gray-300 bg-white px-2 py-1.5 shadow-sm"
          />
        </label>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-lg bg-white py-16 shadow">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
          <p className="text-sm text-gray-600">Loading technicians…</p>
        </div>
      ) : (
      <div className="overflow-x-auto rounded-lg bg-white shadow">
        <div className="flex items-center justify-between border-b px-4 py-3 text-sm text-gray-600">
          <span>
            {nameNeedle
              ? `${visibleItems.length} matching on this page`
              : `Showing ${showingFrom}–${showingTo} of ${data.total}`}
          </span>
          <div className="space-x-2">
            <button
              type="button"
              className="rounded border px-2 py-1 disabled:opacity-40"
              disabled={offset === 0 || loading}
              onClick={() => setFilter({ offset: String(Math.max(0, offset - PAGE_SIZE)) })}
            >
              Previous
            </button>
            <button
              type="button"
              className="rounded border px-2 py-1 disabled:opacity-40"
              disabled={offset + PAGE_SIZE >= data.total || loading}
              onClick={() => setFilter({ offset: String(offset + PAGE_SIZE) })}
            >
              Next
            </button>
          </div>
        </div>
        <table className="min-w-full divide-y divide-gray-100 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Technician</th>
              <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Form</th>
              <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Issue summary</th>
              <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">This week</th>
              <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">This month</th>
              <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {visibleItems.map((row) => {
              const auditsHref = row.technician_id
                ? `/audit/technicians/${encodeURIComponent(row.technician_id)}?${new URLSearchParams({
                    ...(row.tenant_code ? { tenant: row.tenant_code } : {}),
                    ...(row.technician ? { name: row.technician } : {}),
                  }).toString()}`
                : '';
              return (
              <tr key={row.audit_run_id || row.job_id} className="align-middle hover:bg-gray-50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2.5">
                    <TechnicianScoreGauge
                      score={row.score}
                      labelPrefix={row.technician ? `${row.technician} score` : 'Technician score'}
                    />
                    <div className="min-w-0">
                      {auditsHref ? (
                        <Link to={auditsHref} className="font-medium text-blue-700 hover:underline">
                          {row.technician}
                        </Link>
                      ) : (
                        <div className="font-medium text-gray-900">{row.technician}</div>
                      )}
                      <div className="font-mono text-xs text-gray-500">
                        {row.tenant_name ? `${row.tenant_name} · ` : ''}
                        {row.tenant_code}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-900">{row.form_name}</div>
                  <div className="text-xs text-gray-500">
                    {row.job_count > 1 ? `${row.job_count} jobs · ` : ''}
                    {row.form_date || '—'} · job {row.job_id}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="mb-2 inline-block rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
                    {row.fail_count === 1
                      ? '1 finding failed on their latest job'
                      : `${row.fail_count} findings failed on their latest job`}
                  </span>
                  {row.issues?.length ? (
                    <ul className="mt-1 max-w-xs list-disc pl-4 text-gray-700">
                      {row.issues.map((issue) => (
                        <li key={issue}>{issue}</li>
                      ))}
                    </ul>
                  ) : (
                    <div className="mt-1 text-gray-500">No open issues</div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <TechnicianScoreGauge
                    score={typeof row.week_avg === 'number' ? row.week_avg : null}
                    labelPrefix="This week score"
                  />
                  <div className="mt-1 text-xs text-gray-500">{row.week_jobs} jobs</div>
                </td>
                <td className="px-4 py-3">
                  <TechnicianScoreGauge
                    score={typeof row.month_avg === 'number' ? row.month_avg : null}
                    labelPrefix="This month score"
                  />
                  <div className="mt-1 text-xs text-gray-500">{row.month_jobs} jobs</div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1.5">
                    {auditsHref ? (
                      <Link
                        to={auditsHref}
                        className="rounded-md bg-blue-600 px-2 py-1 text-xs font-medium text-white hover:bg-blue-700"
                      >
                        Audits
                      </Link>
                    ) : null}
                    {/^\d+$/.test(row.technician_id || '') && row.tenant_code ? (
                      <button
                        type="button"
                        className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                        onClick={() => loadPreview(row)}
                      >
                        Send Progress Email
                      </button>
                    ) : null}
                  </div>
                </td>
              </tr>
              );
            })}
            {(data.items || []).length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                  No scored audit runs match these filters.
                </td>
              </tr>
            )}
            {(data.items || []).length > 0 && visibleItems.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                  No technicians on this page match that name.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      )}
      {emailTarget ? (
        <ProgressEmailModal
          preview={preview}
          loading={previewLoading}
          sending={sending}
          error={previewError}
          onCancel={() => {
            setEmailTarget(null);
            setPreview(null);
            setPreviewError('');
          }}
          onRefresh={() => loadPreview(emailTarget)}
          onSend={() => {
            if (!preview?.preview_id) return;
            setSending(true);
            sendProgressPreview(preview.preview_id)
              .then((result) => {
                if (result.status === 'sent') {
                  toast.success('Progress email sent');
                  setEmailTarget(null);
                  setPreview(null);
                } else if (result.status === 'failed') {
                  setPreviewError(result.failure_message || 'The email could not be sent');
                  setPreview(result);
                } else {
                  toast.success(`Progress email ${result.status}`);
                  setEmailTarget(null);
                  setPreview(null);
                }
              })
              .catch((err) => setPreviewError(apiMessage(err, 'The email could not be sent')))
              .finally(() => setSending(false));
          }}
        />
      ) : null}
    </div>
  );
};

export default JobAuditTechnicians;
